import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import GreyBox from '../GreyBox';
import { Controller, useForm } from 'react-hook-form';
import { SdmTextfieldV5 } from '../styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { useCopyToClipboard } from '../useCopyToClipboard';
import { useMatchMakingStore } from '../store';

const defaultValue = {
  name: undefined,
  password: undefined,
};

const CreateContent = ({ onClose, onCreateSubmit }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.MatchMakingV2(intl).Dialog.create;
  const i18nCommon = translations.MatchMakingV2(intl).Common;
  const i18nAlerts = translations.MatchMakingV2(intl).Alerts;
  const i18nLegacy = translations.Agenda(intl).forms;
  const i18nMap = translations.MatchMakingV2(intl).MappedErrors;
  const i18nCustomError = translations.MatchMakingV2(intl).CustomErrors;
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleSubmit, errors, control, getValues, watch } = useForm({
    defaultValues: defaultValue,
  });
  const { copy } = useCopyToClipboard();
  const wPassword = watch('password');

  const closeAlert = (sev) =>
    useMatchMakingStore.setState({ alertData: { severity: sev } });

  const openAlert = ({ title, message = '', sev }) => {
    useMatchMakingStore.setState({
      alertData: {
        title,
        message,
        onClose: () => closeAlert(sev),
        severity: sev,
      },
    });
  };

  //  States
  const [showPassword, setShowPassword] = useState(false);

  const passwordValidationPattern = {
    value:
      // eslint-disable-next-line no-useless-escape
      /^(?=.*[a-z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])(?=.{8,99}$).*/,
    message: i18nMap.Code9,
  };

  const passwordEmptySpace = {
    value:
      // eslint-disable-next-line no-useless-escape
      /\s/g,
    message: i18nCustomError.emptySpace,
  };

  // Functions
  const onCopyToClipboard = useCallback(
    (text) => {
      if (text) {
        openAlert({ title: i18nAlerts.copy, sev: 'success' });
        copy(text);
      }
    },
    [copy]
  );

  const onSubmit = (data) => {
    const { name, password } = data;
    const trimmedName = name.trim();
    const trimmedPassword = password.trim();
    onCreateSubmit?.({ name: trimmedName, password: trimmedPassword });
  };

  // Renders
  const renderError = (key) => {
    if (
      errors[key] &&
      (errors[key]?.type === 'required' ||
        errors[key]?.type === 'matchPattern' ||
        errors[key]?.type === 'spacePattern')
    ) {
      return (
        <Typography
          style={{ color: `${theme.palette.primary.main}`, marginTop: -0 }}
          variant={'body2'}
        >
          {errors[key]?.type === 'matchPattern' ||
          errors[key]?.type === 'spacePattern'
            ? errors[key]?.message
            : i18nLegacy.fields.error}
        </Typography>
      );
    }
    return null;
  };

  const Header = () => (
    <Stack gap={'5px'}>
      <Typography variant={'m-bold'} textAlign={'start'}>
        {i18n.title}
      </Typography>
      <Typography variant={'body2'} textAlign={'start'}>
        {i18n.subtitle}
      </Typography>
    </Stack>
  );

  const Buttons = () => (
    <Stack
      flexDirection={isSmDown ? 'column-reverse' : 'row'}
      gap={isSmDown ? '20px' : '25px'}
      justifyContent={'flex-start'}
    >
      <Button variant="outlined" color="primary" size="small" onClick={onClose}>
        {i18n.buttons.cancel}
      </Button>
      <Button variant="contained" color="primary" size="small" type="submit">
        {i18n.buttons.create}
      </Button>
    </Stack>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Stack gap={'25px'}>
        <Header />
        <Stack gap={'5px'}>
          <Controller
            name={'name'}
            control={control}
            rules={{ required: true }}
            render={(props) => (
              <SdmTextfieldV5
                variant="standard"
                onChange={(e) => {
                  props.onChange(e.target.value);
                }}
                label={`${i18nCommon.userName} *`}
                role="presentation"
                autoComplete="off"
              />
            )}
          />
          <Typography variant={'xs'} textAlign={'start'}>
            {i18nCommon.userNameHelp}
          </Typography>
          {renderError('name')}
        </Stack>
        <Stack gap={'5px'}>
          <Controller
            name={'password'}
            control={control}
            rules={{
              required: true,
              validate: {
                matchPattern: (v) => {
                  if (!passwordValidationPattern.value.test(v)) {
                    return passwordValidationPattern.message;
                  }
                },
                spacePattern: (v) => {
                  if (passwordEmptySpace.value.test(v)) {
                    return passwordEmptySpace.message;
                  }
                },
              },
            }}
            render={(props) => (
              <SdmTextfieldV5
                variant="standard"
                onChange={(e) => {
                  props.onChange(e.target.value);
                }}
                label={`${i18nCommon.password} *`}
                type={showPassword ? 'text' : 'password'}
                role="presentation"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack flexDirection={'row'} gap={'5px'}>
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon
                              fontSize="small"
                              color="primary"
                            />
                          ) : (
                            <VisibilityIcon fontSize="small" color="primary" />
                          )}
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            onCopyToClipboard(getValues('password'))
                          }
                          edge="end"
                          disabled={!wPassword}
                        >
                          <ContentCopyRoundedIcon
                            fontSize="small"
                            color="primary"
                            sx={{
                              ...(!wPassword && {
                                opacity: 0.2,
                              }),
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Typography variant={'xs'} textAlign={'start'}>
            {i18nCommon.passwordHelp} {`!@#$%^&*(),.?":_{}-|<>)`}
          </Typography>
          {renderError('password')}
        </Stack>
        <GreyBox />
        <Buttons />
      </Stack>
    </form>
  );
};
export default CreateContent;
